import { css } from '@emotion/react';
import { useState, useEffect } from 'react';
import { Grid, Box, FormControl, Select, Typography, MenuItem } from '@material-ui/core';
import Button from 'components/shared/Button/Button';
import { i18n } from 'lib/i18n';
import { setOpacity } from 'styles/utils';
import { RCLeague, RCLeagueSeason } from 'types/coreEntites/Leagues';
import { getDatesRange, apiDateStringToDate, getActivityDayTime } from 'lib/dates';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import { useResetRecoilState, useSetRecoilState } from 'recoil';
import { checkoutStore } from 'stores/programCheckoutStore';
import router from 'next/router';
import { checkoutFlow } from 'components/Checkout/checkoutFlow';
import { useColors } from 'hooks/useColors';
import { IColors } from 'types/colors';

import { useWindowSize } from 'hooks/useWindowSize';
import { getIsPastSeason } from 'lib/seasonUtils';
import { AlertBox } from '../../Checkout/program/step/lib/application.util';
import dayjs from 'dayjs';

const boxCss = (colors: IColors) => css`
	width: 100%;
	min-height: 437px;
	display: flex;
	flex-direction: column;
	border-radius: 2px;
	background-color: ${colors.bg_white};
	border: solid 1px ${colors.bg_border_primary};
	border-top: none;
	box-shadow: 0 2px 22px 8px ${setOpacity(colors.bg_text_primary || '', 0.08)};
`;

const infoIconCss = css`
	font-style: italic;
	vertical-align: text-top;
`;

const topThinRowCss = (colors: IColors) => css`
	width: 100%;
	height: 5px;
	background-image: linear-gradient(to left, ${colors.bg_system}, ${colors.bg_system} 0%);
`;

const ctaBtnCss = css`
	&.MuiButton-root {
		margin-top: 20px;
		width: -webkit-fill-available;
		font-size: 1.6rem;
		font-weight: 600;
	}
`;

const detailTitleCss = (colors: IColors) => css`
	&.MuiTypography-root {
		color: ${colors.tx_background_overlay};
		font-size: 1.4rem;
		font-weight: 600;
	}
`;

const detailValueCss = (colors: IColors) => css`
	&.MuiTypography-root {
		color: ${colors.tx_text_primary};
		font-size: 2rem;
		font-weight: 600;
	}
`;

const earlyValueCss = (colors: IColors) => css`
	&.MuiTypography-root {
		color: ${colors.btnGreenBg};
		font-size: 2rem;
		font-weight: bold;
	}
`;

const priceCss = (colors: IColors) => css`
	&.MuiTypography-root {
		color: ${colors.tx_text_primary};
		font-size: 2rem;
		font-weight: 600;
		line-height: 1.43;
	}
`;

const earlyPriceCss = (colors: IColors) => css`
	&.MuiTypography-root {
		color: ${colors.btnGreenBg};
		font-size: 2rem;
		font-weight: bold;
		line-height: 1.43;
	}
`;

const detailsWrapperCss = css`
	padding: 9px 40px 9px 0;
`;

const seasonFormControlCss = css`
	width: 100%;
`;

const seasonSelectCss = (colors: IColors) => css`
	background-color: ${colors.bg_background_primary};
	text-indent: 12px;
	font-size: 1.6rem;
	border: none;
	height: 50px;
	&.MuiInput-root {
		margin-top: 0;
	}
	.MuiSelect-icon {
		background-color: ${colors.bg_text_teriaty};
		top: 0;
		width: 44px;
		height: 100%;
		color: ${colors.tx_white};
		padding: 12px;
	}
`;

const firstRowCss = css`
	display: flex;
	justify-content: flex-end;
`;

const timeNoteCss = (earlyBird: boolean, colors: IColors) => css`
	width: 126px;
	height: 31px;
	${earlyBird ? `background-color: ${colors.btnGreenBg};` : ''}
	border-radius: 2px;
	font-size: 1.4rem;
	font-weight: bold;
	color: ${colors.tx_white};
	margin-right: 30px;

	display: flex;
	align-items: center;
	justify-content: center;
`;

const contentWrapperCss = (emptyState?: boolean) => css`
	display: flex;
	flex-direction: column;
	${emptyState ? `height: 380px;` : ''}
	justify-content: center;
	padding: 15px 30px;
`;

const registrationTitleCss = (colors: IColors) => css`
	margin-top: 10px;

	font-size: 2.4rem;
	font-stretch: normal;
	font-style: normal;
	line-height: normal;
	letter-spacing: normal;
	color: ${colors.tx_text_primary};
`;

const centeredWrapperCss = css`
	display: flex;
	justify-content: center;
`;

const priceCategoryCss = (colors: IColors) => css`
	&.MuiTypography-root {
		font-size: 1.4rem;
		line-height: 1.43;
		color: ${colors.tx_text_primary};
		font-weight: 500;
	}
`;

const detailWrapperCss = (colors: IColors, teamPrice?: number, singlePrice?: number, isEarlyBird?: boolean) => css`
	text-align: ${(teamPrice || singlePrice) && 'center'};
	background-clip: padding-box;
	background-color: ${isEarlyBird
		? setOpacity(colors.btnGreenBg || '', 0.1)
		: (teamPrice || singlePrice) && colors.bg_background_primary};
	padding-top: 8px;
	padding-bottom: 8px;
`;

const Detail = (
	title: string,
	value: string,
	colors: IColors,
	isMobile: boolean,
	teamPrice?: number,
	singlePrice?: number,
	isEarlyBird?: boolean
) => {
	return (
		<Grid item sm={isMobile ? 12 : 4}>
			<div css={detailWrapperCss(colors, teamPrice, singlePrice, isEarlyBird)}>
				<Typography css={detailTitleCss(colors)}>
					{title} {(teamPrice || singlePrice) && <InfoIcon fontSize="inherit" css={infoIconCss} />}
				</Typography>
				<Typography
					style={teamPrice || singlePrice ? { fontSize: '1.4rem' } : {}}
					css={isEarlyBird ? earlyValueCss(colors) : detailValueCss(colors)}
				>
					{value}
				</Typography>
				<Grid container>
					{singlePrice && (
						<Grid item sm={6}>
							<Typography css={priceCategoryCss(colors)}>{i18n.leagues.individual}</Typography>
							<Typography css={isEarlyBird ? earlyPriceCss(colors) : priceCss(colors)}>${singlePrice}</Typography>
						</Grid>
					)}
					{teamPrice && (
						<Grid item sm={singlePrice ? 6 : 12}>
							<Typography css={priceCategoryCss(colors)}>{i18n.leagues.team}</Typography>
							<Typography css={isEarlyBird ? earlyPriceCss(colors) : priceCss(colors)}>${teamPrice}</Typography>
						</Grid>
					)}
				</Grid>
			</div>
		</Grid>
	);
};

const isEarlyBird = (season: RCLeagueSeason) => {
	return dayjs(apiDateStringToDate(`${season.earlyRegistrationEnds}`)).isBefore(dayjs());
};

interface Props {
	seasons: RCLeagueSeason[];
	league: RCLeague;
	sport: number;
}

function RegistrationPicker({ seasons, league, sport }: Props) {
	const { isMobile } = useWindowSize();
	const { colors } = useColors();
	const initialSeason = seasons.length === 1 ? seasons[0] : undefined;

	const [selectedSeason, setSelectedSeason] = useState<RCLeagueSeason | undefined>(initialSeason);
	const isPastSeason = getIsPastSeason(selectedSeason);

	const resetCheckoutState = useResetRecoilState(checkoutStore.checkout);
	const setCheckoutState = useSetRecoilState(checkoutStore.checkout);

	const seasonChangeHandler = (event: React.ChangeEvent<{ value: unknown }>) => {
		const seasonId = event.target.value as number;
		setSelectedSeason(seasons.find(seasonItem => seasonItem.id === seasonId));
	};

	useEffect(() => {
		if (seasons.length === 1) {
			setSelectedSeason(seasons[0]);
		}
	}, [seasons]);
	const bookNowHandler = () => {
		if (!selectedSeason) {
			return;
		}

		// Reset the checkout state to start fresh.
		// Prevents stale state from previous sessions, leaving a cart half through.
		resetCheckoutState();

		// fake league season to be like program season
		selectedSeason.organizationId = league.organizationId;
		selectedSeason.questionnaires = league.questionnaireId ? [league.questionnaireId] : [];

		setCheckoutState(state => {
			return {
				...state,
				currentSport: sport,
				selectedProgram: '',
				selectedSeason,
			};
		});

		router.push(`/checkout/${checkoutFlow.league[0].urlPath}`);
	};

	return (
		<Box css={boxCss(colors)}>
			<div data-aid="RegistrationPicker-top" css={topThinRowCss(colors)} />
			<div data-aid="RegistrationPicker-first" css={firstRowCss}>
				<div css={timeNoteCss(selectedSeason ? isEarlyBird(selectedSeason) : false, colors)}>
					{selectedSeason && isEarlyBird(selectedSeason) && <div>{i18n.programs.registration.title.earlyBird}</div>}
				</div>
			</div>
			<div data-aid="RegistrationPicker-over" css={contentWrapperCss(!((selectedSeason?.id || 0) > 0))}>
				{isPastSeason ? <AlertBox message={i18n.programs.registration.registrationOver} /> : null}
				<h1  data-testid="RegistrationPicker-3D0FDF" css={registrationTitleCss(colors)}>{i18n.leagueSeasons.registraion.toUpperCase()}</h1>
				<div css={centeredWrapperCss}>
					<FormControl css={seasonFormControlCss}>
						<Select
							data-aid="RegistrationPicker-select"
							labelId="leagueSeason"
							id="leagueSeason"
							value={selectedSeason?.id || 0}
							onChange={seasonChangeHandler}
							disableUnderline
							css={seasonSelectCss(colors)}
							IconComponent={ExpandMoreIcon}
						>
							{seasons.length !== 1 && <MenuItem value="0">{i18n.leagues.emptySeason}</MenuItem>}

							{seasons.map(season => (
								<MenuItem key={season.id} value={season.id}>
									{season.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</div>

				{selectedSeason ? (
					<Grid css={detailsWrapperCss} container spacing={2}>
						{Detail(
							i18n.programs.season.dates,
							`${getDatesRange(
								`${selectedSeason.startDateString ?? selectedSeason.startDate}`,
								`${selectedSeason.endDateString ?? selectedSeason.endDate}`
							)}`,
							colors,
							isMobile
						)}
						{Detail(
							i18n.event.eventTime,
							getActivityDayTime(
								selectedSeason.activityTimes.map(time => time.dayOfWeek - 2),
								selectedSeason.activityTimes[0].open,
								selectedSeason.activityTimes[0].close
							),
							colors,
							isMobile
						)}
						{Detail('', '', colors, isMobile)}
						{selectedSeason.earlyRegistrationEnds &&
							Detail(
								i18n.season.earlyBirdRegistration,
								`${i18n.programs.registration.throughDate} ${getDatesRange(`${selectedSeason.earlyRegistrationEnds}`)}`,
								colors,
								isMobile,
								selectedSeason.priceEarlyTeam,
								selectedSeason.priceEarlySingle,
								true
							)}
						{selectedSeason.regularRegistrationEnds &&
							Detail(
								i18n.season.regularRegistration,
								`${i18n.programs.registration.throughDate} ${getDatesRange(
									`${selectedSeason.regularRegistrationEnds}`
								)}`,
								colors,
								isMobile,
								selectedSeason.priceRegularTeam,
								selectedSeason.priceRegularSingle
							)}
						{selectedSeason.lateRegistrationEnds &&
							Detail(
								i18n.season.lateRegistration,
								`${i18n.programs.registration.throughDate} ${getDatesRange(`${selectedSeason.lateRegistrationEnds}`)}`,
								colors,
								isMobile,
								selectedSeason.priceLateTeam,
								selectedSeason.priceLateSingle
							)}
					</Grid>
				) : null}
				<Box>
					<Button
						data-aid="button-RegistrationPicker-book"
						css={ctaBtnCss}
						variant="contained"
						color="secondary"
						size="large"
						onClick={bookNowHandler}
						disabled={isPastSeason || (selectedSeason ? selectedSeason.id === 0 : true)}
					>
						{i18n.programs.registerNowButtonText}
					</Button>
				</Box>
			</div>
		</Box>
	);
}

export default RegistrationPicker;
